import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useHandoffRedemption } from '@bloo-team/core-shell'
import { getSession, defaultLocation } from '../../lib/session'
import { DEAD_LINK_COPY, HandoffPanel } from '../../components/auth/HandoffPanel'

/**
 * The DESTINATION half of session handoff (docs/session-handoff.md).
 *
 * The legacy console's switch sends a user here with a one-shot ticket in the
 * path. We spend it, exchange the returned SSID for access-details, and drop
 * the user into the console as if they had signed in — because they had,
 * minutes ago, on the other origin.
 *
 * Everything load-bearing is in `useHandoffRedemption`: fire exactly once, and
 * strip the token from the address bar before the first await. This file is
 * the two things it can show.
 *
 * DEMO MODE (constitution rule 6): a demo build has no backend to redeem
 * against, so the ticket cannot be spent and this route lands on the dead-link
 * panel with a route to sign-in. That is the correct demo behaviour, not a
 * gap — the alternative is fabricating a session from an unverified string,
 * which is a sign-in bypass wearing a demo hat.
 */
function HandoffRedeemPage() {
  const navigate = useNavigate()
  const { token } = Route.useParams()

  const result = useHandoffRedemption(token, () => {
    const session = getSession()
    if (!session) return
    const { clusterId, scopeId } = defaultLocation(session)
    // `replace`, so Back from the first console screen returns to wherever the
    // user was before the switch — never to a handoff route whose ticket is
    // now spent.
    void navigate({ to: '/$clusterId/$scopeId/home', params: { clusterId, scopeId }, replace: true })
  })

  if (result.state === 'failed') {
    return (
      <HandoffPanel
        title="Sign-in link expired"
        action={{ label: 'Go to sign in', onPress: () => void navigate({ to: '/auth/login', replace: true }) }}
      >
        {result.message || DEAD_LINK_COPY}
      </HandoffPanel>
    )
  }

  return <HandoffPanel title="Signing you in">Carrying your session over…</HandoffPanel>
}

export const Route = createFileRoute('/auth/handoff/$token')({
  component: HandoffRedeemPage,
})
